import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { HoursApi } from "@/services/api/hours-api";
import { HoursTable, type HoursTableRow } from "./HoursTable";
import type {
  EnergyStafferDeal,
  EnergyStafferDealResourceResponse,
  EnergyStafferDealSaveRow,
} from "./weekly-hours-types";

interface EnergyStafferDealResourceFormProps {
  isSi: boolean;
  actorUserId?: number;
}

interface BankerDraftRow {
  analystId: number;
  taskId: number;
  name: string;
  first: string;
  second: string;
  third: string;
  originalFirst: number | null;
  originalSecond: number | null;
  originalThird: number | null;
}

function toInput(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

function toNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function stafferIsOwner(deal: EnergyStafferDeal, actorUserId?: number): boolean {
  if (!actorUserId) return false;
  return !!deal.Members?.some(
    (member) => (member.User?.Id === actorUserId || member.User?.UserId === actorUserId) && member.IsOwner
  );
}

// The deal-centric log gates every banker row on deal-level facts about the
// logged-in staffer: not overdue, staffer is a project member, staffer is an owner.
function dealLockedReason(deal: EnergyStafferDeal, actorUserId?: number): string | null {
  if (deal.IsOverdue) return "Extend the deal due date in TRIYO to revise hours.";
  if (deal.IsProjectMember === false) return "Join this deal before revising banker hours.";
  if (!stafferIsOwner(deal, actorUserId)) return "Only a deal owner can revise banker hours.";
  return null;
}

function bankerRows(deal: EnergyStafferDeal): BankerDraftRow[] {
  return (deal.JuniorBankers ?? []).map((banker) => ({
    analystId: banker.AnalystId,
    taskId: Number(banker.TaskId),
    name: banker.Level ? `${banker.Banker ?? "Banker"} · ${banker.Level}` : (banker.Banker ?? "Banker"),
    first: toInput(banker.LastWeekHours),
    second: toInput(banker.LastWeekendHours),
    third: toInput(banker.CurrentWeekHours),
    originalFirst: banker.LastWeekHours,
    originalSecond: banker.LastWeekendHours,
    originalThird: banker.CurrentWeekHours,
  }));
}

function rowChanged(row: BankerDraftRow): boolean {
  return (
    toNumber(row.first) !== row.originalFirst ||
    toNumber(row.second) !== row.originalSecond ||
    toNumber(row.third) !== row.originalThird
  );
}

function rowInvalid(row: BankerDraftRow): boolean {
  return [row.first, row.second, row.third].some((value) => {
    if (!value.trim()) return false;
    const parsed = Number(value);
    return !Number.isFinite(parsed) || parsed < 0;
  });
}

function formatHours(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

const RESOURCE_LABELS = ["Last weekday hours", "Last weekend hours", "Current week estimate"] as const;
const FIELDS = ["first", "second", "third"] as const;

export function EnergyStafferDealResourceForm({ isSi, actorUserId }: EnergyStafferDealResourceFormProps): JSX.Element {
  const [deals, setDeals] = useState<EnergyStafferDeal[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [rows, setRows] = useState<BankerDraftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const applyDeals = useCallback((response: EnergyStafferDealResourceResponse, preferredProjectId: number | null) => {
    const nextDeals = response.Deals ?? [];
    setDeals(nextDeals);
    const selected = nextDeals.find((deal) => deal.ProjectId === preferredProjectId) ?? nextDeals[0] ?? null;
    setSelectedProjectId(selected?.ProjectId ?? null);
    setRows(selected ? bankerRows(selected) : []);
  }, []);

  const load = useCallback(
    async (preferredProjectId: number | null = null) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const response = await HoursApi.getEnergyStafferDealResourceLog(isSi);
        if (requestId === requestIdRef.current) applyDeals(response, preferredProjectId);
      } catch (reason: unknown) {
        if (requestId === requestIdRef.current) {
          setDeals([]);
          setRows([]);
          setError(reason instanceof Error ? reason.message : "Could not load the deal resourcing log.");
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [applyDeals, isSi]
  );

  useEffect(() => {
    void load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  const selectedDeal = deals.find((deal) => deal.ProjectId === selectedProjectId) ?? null;
  const lockedReason = selectedDeal ? dealLockedReason(selectedDeal, actorUserId) : null;
  const editable = selectedDeal != null && lockedReason == null;
  const changedRows = useMemo(() => rows.filter(rowChanged), [rows]);
  const invalid = rows.some(rowInvalid);
  const canSubmit = editable && changedRows.length > 0 && !invalid && !saving;

  const selectDeal = (projectId: number) => {
    const deal = deals.find((candidate) => candidate.ProjectId === projectId) ?? null;
    setSelectedProjectId(deal?.ProjectId ?? null);
    setRows(deal ? bankerRows(deal) : []);
    setError(null);
    setSuccess(null);
  };

  const update = (taskId: number, field: "first" | "second" | "third", value: string) => {
    setRows((current) => current.map((row) => (row.taskId === taskId ? { ...row, [field]: value } : row)));
    setSuccess(null);
  };

  const submit = async () => {
    if (!canSubmit || selectedDeal == null) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: EnergyStafferDealSaveRow[] = changedRows.map((row) => ({
        AnalystId: row.analystId,
        TaskId: row.taskId,
        LastWeekHours: toNumber(row.first),
        LastWeekendHours: toNumber(row.second),
        CurrentWeekHours: toNumber(row.third),
      }));
      await HoursApi.saveEnergyStafferDealResourceLog(isSi, payload);
      await load(selectedDeal.ProjectId);
      setSuccess(`Weekly hours saved for ${selectedDeal.ProjectName}.`);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Could not save weekly hours.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="weekly-hours__state" role="status">
        <FluentSpinner label="Loading deal resourcing log…" />
      </div>
    );
  }

  if (error && deals.length === 0) {
    return (
      <div className="weekly-hours__state" role="alert">
        <p>{error}</p>
        <button type="button" className="weekly-hours__secondary-button" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="weekly-hours__form">
      {deals.length === 0 ? (
        <div className="weekly-hours__empty">No deals are available in this staffer resourcing log.</div>
      ) : (
        <label className="weekly-hours__banker-select">
          <span>Deal</span>
          <select value={selectedProjectId ?? ""} onChange={(event) => selectDeal(Number(event.target.value))}>
            {deals.map((deal) => (
              <option key={deal.ProjectId} value={deal.ProjectId}>
                {deal.ProjectName}
              </option>
            ))}
          </select>
        </label>
      )}

      {selectedDeal ? (
        rows.length === 0 ? (
          <div className="weekly-hours__empty">This deal has no junior bankers with weekly hour tasks.</div>
        ) : (
          <>
            <HoursTable
              ariaLabel="Deal resourcing log"
              nameHeader="Banker"
              columns={[
                { short: "M–F", full: RESOURCE_LABELS[0] },
                { short: "S–S", full: RESOURCE_LABELS[1] },
                { short: "Est.", full: RESOURCE_LABELS[2] },
              ]}
              rows={rows.map((row): HoursTableRow => {
                const validation = rowInvalid(row) ? (
                  <p className="weekly-hours__validation" role="alert">
                    Enter hours greater than or equal to zero.
                  </p>
                ) : null;
                return {
                  key: `${row.analystId}-${row.taskId}`,
                  id: row.taskId,
                  name: row.name,
                  // Live status first — the deal's "Live Deal" toggle, resolved by the
                  // API to "Live"/"Non-Live". GroupName is the sector in this payload.
                  context: selectedDeal.Live || selectedDeal.Company || selectedDeal.GroupName || "Energy deal",
                  locked: !editable,
                  values: [row.first, row.second, row.third],
                  editable: [editable, editable, editable],
                  ariaLabels: [
                    `${RESOURCE_LABELS[0]} for ${row.name}`,
                    `${RESOURCE_LABELS[1]} for ${row.name}`,
                    `${RESOURCE_LABELS[2]} for ${row.name}`,
                  ],
                  notes: validation,
                };
              })}
              totals={[
                formatHours(rows.reduce((sum, row) => sum + (toNumber(row.first) ?? 0), 0)),
                formatHours(rows.reduce((sum, row) => sum + (toNumber(row.second) ?? 0), 0)),
                `${formatHours(rows.reduce((sum, row) => sum + (toNumber(row.third) ?? 0), 0))}h`,
              ]}
              onValueChange={(taskId, index, value) => update(taskId, FIELDS[index], value)}
            />
            {lockedReason ? <p className="weekly-hours__lock-note">{lockedReason}</p> : null}
          </>
        )
      ) : null}

      {error && deals.length > 0 ? (
        <div className="weekly-hours__message weekly-hours__message--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="weekly-hours__message weekly-hours__message--success" role="status">
          <CheckmarkCircleRegular />
          {success}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="weekly-hours__actions">
          <button
            type="button"
            className="weekly-hours__secondary-button"
            disabled={saving}
            onClick={() => void load(selectedProjectId)}
          >
            Reset
          </button>
          <button
            type="button"
            className="weekly-hours__primary-button"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {saving ? "Saving…" : "Submit changes"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
